import React, { CSSProperties, useCallback, useEffect, useState } from "react";
import {
    ConfigProvider, Dropdown, MenuProps, Row, Space, Spin, theme,
    AutoComplete, Tooltip, Input, Button
} from "antd";
import { DownOutlined } from '@ant-design/icons';
import { StyleUtils } from "../utils/Style";
import { ApiCode, Currency } from "../data/common";
import { UserInfoManager } from "../utils/UserInfoManager";
import { GlobalUtil } from "../utils/GlobalText";
import enUS from 'antd/locale/en_US';
import zhCN from 'antd/locale/zh_CN';
import Api from "../utils/Api";
import _ from 'lodash';
import events from "../utils/events";
import { AppEvent } from "../data/events";

// Pool Button
interface PoolButtonlProps {
    text: string
}
const PoolButton: React.FC<PoolButtonlProps> = (props) => {
    return (
        <div style={{
            borderColor: "",
            color: "",
            paddingLeft: StyleUtils.getWidth(10),
            paddingRight: StyleUtils.getWidth(10),
            paddingTop: StyleUtils.getWidth(4),
            paddingBottom: StyleUtils.getWidth(4),
            background: "#FAFAFF"
        }} />
    );
};

// Main Text
interface AppMainTextProps {
    text: string
    fontSize?: string
    style?: CSSProperties
}
const AppMainText: React.FC<AppMainTextProps> = (props) => {
    return (
        <div style={{
            WebkitTextFillColor: "transparent",
            WebkitBackgroundClip: "text",
            display: "inline-block",
            fontWeight: 500,
            backgroundImage: StyleUtils.LG_FONT_COLOR,
            fontSize: props.fontSize ?? "2.4rem",
            ...StyleUtils.textWrap(),
            ...props.style
        }}>
            {props.text ?? ""}
        </div>
    );
};

// App Button
interface AppButtonProps {
    onClick?: () => void
    text: string
    loading?: boolean
    style?: CSSProperties
    disabled?: boolean
    canClick?: boolean
}
const AppButton: React.FC<AppButtonProps> = (props) => {
    return (
        <Button
            style={{
                cursor: "pointer",
                background: props.disabled ? StyleUtils.theme().disabledButtoinColor : StyleUtils.theme().mainButtonColor,
                borderRadius: "8px",
                color: props.disabled ? (StyleUtils.isLight ? "white" : "#899199") : "white",
                fontSize: "0.88rem",
                alignItems: "center",
                justifyContent: "center",
                display: "flex",
                borderColor: 'none',
                fontWeight: "500",
                padding: "8px 12px",
                ...props?.style
            }}
            type="primary"
            loading={props.loading}
            onClick={() => {
                if (!props.disabled || props.canClick) {
                    props.onClick && props.onClick()
                }
            }}
        >
            {props.text}
        </Button>
    );
};

// Theme Config Providers
interface AntdViewProps {
    children?: React.ReactNode;
}
const AntdInput: React.FC<AntdViewProps> = (props) => {
    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: StyleUtils.theme().mainButtonColor,
                    colorBorder: "",
                    colorBgContainer: StyleUtils.theme().boardBg,
                    colorTextPlaceholder: StyleUtils.theme().fontColor_placeholder,
                    colorText: StyleUtils.theme().fontColor_1,
                },
                algorithm: StyleUtils.isLight ? theme.darkAlgorithm : theme.defaultAlgorithm
            }}
        >
            {props.children}
        </ConfigProvider>
    );
};

const AntdButton: React.FC<AntdViewProps> = (props) => {
    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: StyleUtils.theme().mainButtonColor,
                    colorBorder: "",
                    colorTextDisabled: StyleUtils.theme().mainButtonColorFont_Disable,
                    colorBgContainerDisabled: StyleUtils.theme().mainButtonDisableColor
                }
            }}
        >
            {props.children}
        </ConfigProvider>
    );
};

const AntdCard: React.FC<AntdViewProps> = (props) => {
    return (
        <ConfigProvider
            theme={{
                token: {
                    colorBorderSecondary: StyleUtils.theme().boardBg,
                    colorPrimary: StyleUtils.theme().mainButtonColor,
                    colorBorder: "",
                    colorBgContainer: StyleUtils.theme().boardBg,
                }
            }}
        >
            {props.children}
        </ConfigProvider>
    );
};

const AntdSelect: React.FC<AntdViewProps> = (props) => {
    return (
        <ConfigProvider
            theme={{
                algorithm: StyleUtils.isLight ? theme.darkAlgorithm : theme.defaultAlgorithm
            }}
        >
            {props.children}
        </ConfigProvider>
    );
};

const AntdTable: React.FC<AntdViewProps> = (props) => {
    return (
        <ConfigProvider
            locale={GlobalUtil.getDefaultLanguage() === 'en_US' ? enUS : zhCN}
            theme={{
                token: {
                    colorBgContainer: StyleUtils.theme().boardBg,
                    colorBorder: StyleUtils.theme().boardBg,
                    colorBorderSecondary: StyleUtils.theme().boardBg,
                    colorText: StyleUtils.theme().fontColor_1,
                    colorBgMask: StyleUtils.theme().modalMaskColor,
                },
                algorithm: StyleUtils.isLight ? theme.darkAlgorithm : theme.defaultAlgorithm
            }}
        >
            {props.children}
        </ConfigProvider>
    );
};

const AntdDatePicker: React.FC<AntdViewProps> = (props) => {
    return (
        <ConfigProvider
            theme={{
                algorithm: StyleUtils.isLight ? theme.darkAlgorithm : theme.defaultAlgorithm
            }}
        >
            {props.children}
        </ConfigProvider>
    );
};

// Search Feature Component
const SearchFeat = ({ data, isShare }: any) => {
    const [value, setValue] = useState('');
    const [pageLoading, setPageLoading] = useState(false);
    const [shareAddress, setShareAddress] = useState<{ value: string; label: any }[]>([]);

    const handleChange = (value: string) => {
        setValue(value);
        if (!value) setShareAddress([]);
    };

    const handleSelect = () => setValue('');

    const handleSearch = async (value: string) => {
        if (!value) return;
        try {
            const res = await Api.getShareAddress(value);
            if (res.code === ApiCode.SUCCESS) {
                setShareAddress([{
                    value: res.data?.requestUrl || '',
                    label: (
                        <Tooltip title={res.data?.requestUrl}>
                            <a href={res.data?.requestUrl} target='_blank'>{res.data?.requestUrl}</a>
                        </Tooltip>
                    ),
                }]);
            } else {
                setShareAddress([{
                    value: '',
                    label: (
                        <Tooltip title={GlobalUtil.t("invalid_address")}>
                            {GlobalUtil.t("invalid_address")}
                        </Tooltip>
                    )
                }]);
            }
        } catch (error) {
            // handle error if needed
        }
    };

    const currency = UserInfoManager.currency || localStorage.getItem('currency');
    const shareCurrency = localStorage.getItem('shareCurrency');
    if (!data?.length) return <div />;

    let selectIndex = (data as Currency[]).findIndex((v) => {
        return isShare ? v.code === shareCurrency : v.code === currency;
    });

    const items: MenuProps['items'] = data.map((v: any, index: number) => ({
        key: index,
        label: (
            <div
                onClick={async () => {
                    setPageLoading(true);
                    if (!isShare) {
                        await UserInfoManager.updateCurrency(v.code);
                    } else {
                        await UserInfoManager.updateShareCurrency(v.code);
                    }
                    setPageLoading(false);
                }}
                style={{ display: "flex", alignItems: "center", color: StyleUtils.theme().fontColor_1 }}
            >
                <img style={{ width: 19, height: 19, marginRight: 4 }} src={v?.icon} alt="" />
                {v?.name}
            </div>
        )
    }));

    events.on(AppEvent.REFRES, ({ currency, account }: any) => {
        if (currency || account) setValue('');
    });

    return (
        <div id="search-feat" style={{ display: "flex", alignItems: "center", flex: "auto", marginRight: "1.5rem" }}>
            <div style={{ display: "flex", width: "100%" }}>
                <div id="currencys" style={{ minWidth: 80, position: 'relative' }}>
                    <Dropdown menu={{ items }} trigger={['click']} getPopupContainer={() => document.getElementById('currencys')!}>
                        <div style={{
                            background: StyleUtils.isLight ? StyleUtils.theme().home_guide_item_color_1 : StyleUtils.theme().mainColor2,
                            borderRadius: "8px 0 0 8px",
                            cursor: "pointer"
                        }}>
                            <Spin spinning={pageLoading}>
                                <Space align="center" style={{
                                    color: StyleUtils.theme().fontColor_1,
                                    fontSize: StyleUtils.getFont(12),
                                    padding: "9px"
                                }}>
                                    <Row>
                                        <img style={{ width: 18, marginRight: 4 }} src={data[selectIndex]?.icon} alt="" />
                                    </Row>
                                    {data[selectIndex]?.name}
                                    <DownOutlined />
                                </Space>
                            </Spin>
                        </div>
                    </Dropdown>
                </div>

                <div className='head-search' style={{
                    display: "flex",
                    flex: "auto",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: `1px solid ${StyleUtils.isLight ? StyleUtils.theme().home_guide_item_color_1 : StyleUtils.theme().mainColor2}`,
                    padding: "0 8px",
                    borderRadius: "0 8px 8px 0"
                }}>
                    <AntdInput>
                        <AutoComplete
                            id="search"
                            placeholder={GlobalUtil.t("work.searchTip")}
                            style={{ maxWidth: "98%", flex: "auto", color: StyleUtils.theme().fontColor_2 }}
                            bordered={false}
                            value={value}
                            onChange={handleChange}
                            onSelect={handleSelect}
                            onSearch={handleSearch}
                            options={shareAddress}
                        />
                    </AntdInput>
                    <img
                        style={{ width: 15, height: 15 }}
                        src={StyleUtils.isLight ? "/assets/dark/search.svg" : "/assets/search.svg"}
                        alt=""
                    />
                </div>
            </div>
        </div>
    );
};

export {
    PoolButton,
    AppMainText,
    AppButton,
    AntdInput,
    AntdButton,
    AntdTable,
    AntdCard,
    SearchFeat,
    AntdSelect,
    AntdDatePicker
};